import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { CreditCard, FileSpreadsheet, Receipt as ReceiptIcon, Search, User as UserIcon, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';

export interface CommandItem {
  id: string;
  label: string;
  /** Aranabilir ek anahtar kelimeler (örn. "kart", "card", "credit"). */
  keywords?: string[];
  /** Görsel category etiketi (Sayfalar, Aksiyon, vb.). */
  category?: string;
  icon?: React.ReactNode;
  /** Tıklandığında çalışacak handler (route, action). */
  onSelect: () => void;
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  items: CommandItem[];
  /**
   * Sprint 28c — true ise input min 2 karaktere ulaşınca `/api/search`
   * çağrılır, sonuçlar mevcut static `items` üstüne eklenir.
   */
  enableServerSearch?: boolean;
}

interface SearchHit {
  type: 'transaction' | 'employee' | 'card' | 'receipt' | 'einvoice';
  id: string;
  primary: string;
  secondary?: string;
  href: string;
}

const TYPE_ICON: Record<SearchHit['type'], React.ReactNode> = {
  transaction: <Wallet size={14} />,
  employee: <UserIcon size={14} />,
  card: <CreditCard size={14} />,
  receipt: <ReceiptIcon size={14} />,
  einvoice: <FileSpreadsheet size={14} />,
};

/**
 * Cmd+K / Ctrl+K ile açılan komut paleti.
 * Türkçe-tolerant fuzzy match — büyük/küçük + diakritik (ş→s, ı→i) yokayar.
 */
export function CommandPalette({ open, onClose, items, enableServerSearch }: CommandPaletteProps) {
  const { t } = useTranslation('components');
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [serverHits, setServerHits] = useState<SearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return items;
    return items.filter((item) => {
      const haystack = [item.label, item.category, ...(item.keywords ?? [])]
        .filter(Boolean)
        .map((s) => normalize(String(s)))
        .join(' ');
      return haystack.includes(q);
    });
  }, [query, items]);

  // Server-side global search — debounced 200ms
  useEffect(() => {
    if (!open || !enableServerSearch) {
      setServerHits([]);
      return;
    }
    const q = query.trim();
    if (q.length < 2) {
      setServerHits([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const result = await api.get<{ items: SearchHit[]; total: number }>(`/search?q=${encodeURIComponent(q)}`);
        if (!cancelled) setServerHits(result.items);
      } catch {
        if (!cancelled) setServerHits([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [open, query, enableServerSearch]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(0);
    setQuery('');
    setServerHits([]);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  if (!open) return null;

  // Server hits → CommandItem'a çevir, mevcut item listesine prepend
  const serverItems: CommandItem[] = serverHits.map((h) => ({
    id: `server-${h.type}-${h.id}`,
    label: h.primary,
    category: t(`commandPalette.type.${h.type}`),
    icon: TYPE_ICON[h.type],
    keywords: h.secondary ? [h.secondary] : undefined,
    onSelect: () => navigate(h.href),
  }));

  const combined = [...serverItems, ...filtered];
  const grouped = groupByCategory(combined);
  const allItems = combined;

  function select(item: CommandItem) {
    item.onSelect();
    onClose();
  }

  function onKey(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, allItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = allItems[activeIndex];
      if (item) select(item);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  const node = (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[10vh]"
      style={{ backgroundColor: 'var(--custel-overlay)' }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('commandPalette.dialogLabel')}
        className="w-full max-w-xl overflow-hidden rounded-custel-xl border border-custel-border bg-custel-paper shadow-custel-modal"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
      >
        <div className="flex items-center gap-2 border-b border-custel-border px-4 py-3">
          <Search size={16} className="text-custel-g500" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('commandPalette.placeholder')}
            className="flex-1 bg-transparent text-sm text-custel-ink placeholder:text-custel-g500 focus:outline-none"
            aria-label={t('commandPalette.searchLabel')}
          />
          {searching && (
            <span
              aria-label={t('common:state.loading')}
              className="inline-block h-3 w-3 animate-spin rounded-full border-[1.5px] border-custel-g500 border-t-transparent"
            />
          )}
          <kbd className="hidden rounded border border-custel-border bg-custel-g100 px-1.5 py-0.5 font-mono text-[10px] font-medium text-custel-g500 sm:inline">
            Esc
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto py-1">
          {allItems.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-custel-g500">{t('commandPalette.noResults')}</p>
          ) : (
            Object.entries(grouped).map(([category, group]) => (
              <div key={category}>
                {category !== '__none__' && (
                  <p className="px-4 pb-1 pt-3 font-mono text-[11px] font-medium uppercase tracking-[0.08em] text-custel-g500">
                    {category}
                  </p>
                )}
                {group.map((item) => {
                  const idx = allItems.indexOf(item);
                  const active = idx === activeIndex;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => select(item)}
                      className={cn(
                        'flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors',
                        active ? 'bg-custel-g100 text-custel-ink' : 'text-custel-ink hover:bg-custel-g100',
                      )}
                    >
                      {item.icon && (
                        <span className="text-custel-g500" aria-hidden="true">
                          {item.icon}
                        </span>
                      )}
                      <span className="flex-1">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center justify-between border-t border-custel-border bg-custel-g100 px-4 py-2 font-mono text-[11px] text-custel-g500">
          <div className="flex items-center gap-3">
            <span>
              <kbd className="rounded bg-custel-paper px-1 py-0.5">↑↓</kbd> {t('commandPalette.navigate')}
            </span>
            <span>
              <kbd className="rounded bg-custel-paper px-1 py-0.5">↵</kbd> {t('commandPalette.select')}
            </span>
          </div>
          <span>{t('commandPalette.resultCount', { count: allItems.length })}</span>
        </div>
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

function normalize(s: string): string {
  return s
    .toLocaleLowerCase('tr')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ı/g, 'i')
    .replace(/[ş]/g, 's')
    .replace(/[ğ]/g, 'g')
    .replace(/[ç]/g, 'c')
    .replace(/[ü]/g, 'u')
    .replace(/[ö]/g, 'o');
}

function groupByCategory(items: CommandItem[]): Record<string, CommandItem[]> {
  const out: Record<string, CommandItem[]> = {};
  for (const it of items) {
    const key = it.category ?? '__none__';
    if (!out[key]) out[key] = [];
    out[key].push(it);
  }
  return out;
}
